import { useState } from 'react';
import { Card, EmptyState } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { useAuth } from '../../lib/auth.js';
import { useSalesOverviewQuery } from './api.js';

const PERIODS = [
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 365, label: 'Last 12 months' },
] as const;

const NAV_ITEMS = [
  { key: 'sales-overview', label: 'Sales overview', path: 'M4 20V10M10 20V4M16 20v-7M22 20H2', active: true, isRect: false },
  { key: 'pipeline', label: 'Pipeline', path: 'M9 4v16M15 4v16', active: false, isRect: true },
  { key: 'sources', label: 'Lead sources & attribution', path: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1', active: false, isRect: false },
  { key: 'funnel', label: 'Conversion funnel', path: 'M3 5h18l-7 8v6l-4 2v-8z', active: false, isRect: false },
  { key: 'activity', label: 'Activity', path: 'M3 12h4l3-8 4 16 3-8h4', active: false, isRect: false },
  { key: 'team', label: 'Team performance', path: 'M9 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M16 4.1a4 4 0 0 1 0 7.8M22 21c0-3-1.8-5.6-4.5-6.6', active: false, isRect: false },
] as const;

function money(value: string | number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value));
}

function timeAgo(ms: number): string {
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 min ago';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hr${hours === 1 ? '' : 's'} ago`;
}

/** docs/architecture screen "Reports — Sales overview" — the one report
 * built so far. Matches design/orion-crm-design/rendered-html/Reports.html's
 * layout: a left "Standard reports" nav (only Sales overview is real; the
 * other five report types and the scheduled-digest row stay design-only,
 * same "Soon" convention used elsewhere, since each is its own metrics
 * build this pass didn't do) plus the real sales-overview content.
 *
 * The design's period picker reads "Q3 2026" — this tenant has no fiscal-
 * quarter concept in the data model, so a rolling N-day window plays the
 * same role without inventing a calendar (already the case before this
 * pass). Two things the design shows that aren't built: a second bars
 * series comparing the prior period month-by-month (revenueByMonth only
 * covers the selected window, not a parallel prior-period series), and
 * "Schedule email" (no report-scheduling system exists). */
export function ReportsPage() {
  const { hasPermission } = useAuth();
  const [days, setDays] = useState<number>(90);
  const { data, isLoading, isError, dataUpdatedAt } = useSalesOverviewQuery(days);

  const funnelMax = data?.funnel[0]?.count || 1;
  const overallConversionPct = data && data.funnel[0]?.count ? Math.round(((data.funnel.at(-1)?.count ?? 0) / data.funnel[0].count) * 1000) / 10 : 0;

  const sourceTotals = data?.sourceAttribution.reduce(
    (acc, r) => ({
      leads: acc.leads + r.leads,
      deals: acc.deals + r.deals,
      wonValue: acc.wonValue + Number(r.wonValue),
    }),
    { leads: 0, deals: 0, wonValue: 0 },
  );

  const fastestSource = data?.sourceAttribution.filter((r) => r.avgDaysToClose !== null).sort((a, b) => a.avgDaysToClose! - b.avgDaysToClose!)[0];
  const avgDaysToClose = data?.sourceAttribution.filter((r) => r.avgDaysToClose !== null);
  const overallAvgDays = avgDaysToClose && avgDaysToClose.length > 0 ? avgDaysToClose.reduce((s, r) => s + r.avgDaysToClose!, 0) / avgDaysToClose.length : null;
  const topRep = data?.repWinRates.length ? [...data.repWinRates].sort((a, b) => b.winRatePct - a.winRatePct)[0] : undefined;

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Reports' }]} />
      <main className="flex flex-1 overflow-hidden">
        <nav aria-label="Reports" className="flex w-55 shrink-0 flex-col gap-0.5 border-r border-border bg-surface-subtle p-3">
          <span className="px-2.5 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Standard reports</span>
          {NAV_ITEMS.map((item) => (
            <span
              key={item.key}
              aria-current={item.active ? 'page' : undefined}
              className={`flex h-9 items-center gap-2.5 rounded-control px-2.5 text-sm ${item.active ? 'border border-border bg-surface font-semibold text-ink shadow-card' : 'font-medium text-text-secondary opacity-60'}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={item.active ? 'var(--color-primary)' : '#5E6470'} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {item.isRect && <rect x="3" y="4" width="18" height="16" rx="2" />}
                <path d={item.path} />
              </svg>
              {item.label}
            </span>
          ))}

          <span className="px-2.5 pb-2 pt-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Scheduled</span>
          <span className="flex h-8 items-center gap-2.5 rounded-control px-2.5 text-[13px] text-text-secondary opacity-60">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
            <span className="flex-1">Weekly pipeline digest</span>
            <span className="text-xs text-text-muted">Soon</span>
          </span>

          <div className="flex-1" />
          <div className="flex flex-col gap-1.5 rounded-input border border-dashed border-border-strong p-3">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-text-muted">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New report
            </span>
            <span className="text-xs leading-4.25 text-text-muted">Custom reports are built on the same metrics layer — builder arrives in v1.1.</span>
          </div>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto px-7 py-6">
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">Sales overview</h1>
              <span className="text-[13px] text-text-muted">Sales pipeline · All teams · USD{data ? ` · Updated ${timeAgo(dataUpdatedAt)}` : ''}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex overflow-hidden rounded-control border border-border-strong text-sm font-medium">
                {PERIODS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    onClick={() => setDays(p.days)}
                    className={days === p.days ? 'bg-primary px-3 py-1.5 text-white' : 'bg-surface px-3 py-1.5 text-text-secondary hover:bg-surface-subtle'}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {hasPermission('reports.reports.export') && (
                <a
                  href={`/api/v1/reports/sales-overview/export?days=${days}`}
                  className="inline-flex h-9 items-center gap-2 rounded-control border border-border-strong bg-surface px-3.5 text-sm font-semibold text-ink hover:bg-surface-subtle"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 4v12M7 11l5 5 5-5" />
                    <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                  </svg>
                  Export CSV
                </a>
              )}
            </div>
          </div>

          {isError && (
            <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
              Couldn't load reports. Please try again.
            </div>
          )}
          {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

          {data && (
            <>
              <div className="grid grid-cols-[1.25fr_1fr] gap-4">
                <Card className="flex flex-col gap-3.5 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-0.5">
                      <h2 className="text-[15px] font-semibold text-ink">Conversion funnel</h2>
                      <span className="text-xs text-text-muted">Leads created in this window · stage reached</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[22px] font-[650] leading-none tabular-nums tracking-[-0.02em]">{overallConversionPct}%</span>
                      {data.wonRevenueChangePct !== null && (
                        <span className={`text-xs font-semibold ${data.wonRevenueChangePct >= 0 ? 'text-success' : 'text-danger'}`}>
                          {data.wonRevenueChangePct >= 0 ? '▲' : '▼'} {Math.abs(data.wonRevenueChangePct)}% <span className="font-medium text-text-muted">revenue vs prior period</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {data.funnel.map((stage, i) => {
                      const prev = data.funnel[i - 1];
                      const pct = Math.max((stage.count / funnelMax) * 100, stage.count > 0 ? 4 : 0);
                      const dropPct = prev && prev.count > 0 ? Math.round((stage.count / prev.count) * 1000) / 10 : null;
                      return (
                        <div key={stage.key} className="flex flex-col gap-0.5">
                          {prev && (
                            <span className="pl-24 text-[11px] tabular-nums text-text-muted">
                              ↓ {dropPct}% {stage.label.toLowerCase()} · <span className="font-semibold text-danger">−{prev.count - stage.count}</span>
                            </span>
                          )}
                          <div className="grid h-6.5 grid-cols-[84px_1fr_44px] items-center gap-3">
                            <span className="text-[13px] text-text-secondary">{stage.label}</span>
                            <div className="flex h-5.5 overflow-hidden rounded-xs bg-divider">
                              <div className="rounded-xs bg-primary" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-right text-[13px] font-[650] tabular-nums">{stage.count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                <Card className="flex flex-col gap-3.5 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-0.5">
                      <h2 className="text-[15px] font-semibold text-ink">Won revenue by month</h2>
                      <span className="text-xs text-text-muted">This window's months</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[22px] font-[650] leading-none tabular-nums tracking-[-0.02em]">{money(data.wonRevenueTotal)}</span>
                      {data.wonRevenueChangePct !== null && (
                        <span className={`text-xs font-semibold ${data.wonRevenueChangePct >= 0 ? 'text-success' : 'text-danger'}`}>
                          {data.wonRevenueChangePct >= 0 ? '▲' : '▼'} {Math.abs(data.wonRevenueChangePct)}% <span className="font-medium text-text-muted">vs prior period</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {data.revenueByMonth.every((m) => Number(m.value) === 0) ? (
                    <p className="text-sm text-text-muted">No won opportunities in this window yet.</p>
                  ) : (
                    <div className="flex h-40 items-end gap-3">
                      {data.revenueByMonth.map((m) => {
                        const max = Math.max(...data.revenueByMonth.map((x) => Number(x.value)), 1);
                        const heightPct = Math.max((Number(m.value) / max) * 100, Number(m.value) > 0 ? 4 : 0);
                        return (
                          <div key={m.month} className="flex flex-1 flex-col items-center gap-1.5">
                            <span className="text-[11px] font-[650] tabular-nums text-text-secondary">{Number(m.value) > 0 ? money(m.value) : ''}</span>
                            <div className="flex w-full flex-1 items-end">
                              <div className="w-full rounded-t-xs bg-primary" style={{ height: `${heightPct}%` }} />
                            </div>
                            <span className="text-xs text-text-muted">{m.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>

              <div className="grid min-h-0 flex-1 grid-cols-[1.75fr_1fr] gap-4">
                <Card className="flex flex-col overflow-hidden p-0">
                  <div className="flex h-13 shrink-0 items-center justify-between px-5">
                    <h2 className="text-[15px] font-semibold text-ink">Lead source attribution</h2>
                  </div>
                  {data.sourceAttribution.length === 0 ? (
                    <div className="p-5">
                      <EmptyState title="No leads in this window" description="Attribution shows up once leads with a source are created." />
                    </div>
                  ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-y border-border bg-surface-subtle text-xs font-semibold text-text-muted">
                            <th className="px-5 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-right">Leads</th>
                            <th className="px-3 py-2 text-right">Qualified %</th>
                            <th className="px-3 py-2 text-right">Opportunities</th>
                            <th className="px-3 py-2 text-right">Won value</th>
                            <th className="px-5 py-2 text-right">Avg days to close</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.sourceAttribution.map((row) => (
                            <tr key={row.sourceId ?? row.sourceName} className="border-b border-divider last:border-b-0">
                              <td className="px-5 py-2 font-semibold text-ink">{row.sourceName}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{row.leads}</td>
                              <td className={`px-3 py-2 text-right tabular-nums ${row.qualifiedPct >= 40 ? 'font-semibold text-success' : ''}`}>{row.qualifiedPct}%</td>
                              <td className="px-3 py-2 text-right tabular-nums">{row.deals}</td>
                              <td className="px-3 py-2 text-right font-semibold tabular-nums">{money(row.wonValue)}</td>
                              <td className={`px-5 py-2 text-right tabular-nums ${row.avgDaysToClose && overallAvgDays && row.avgDaysToClose > overallAvgDays * 1.3 ? 'font-semibold text-danger' : 'text-text-secondary'}`}>
                                {row.avgDaysToClose ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {sourceTotals && (
                          <tfoot>
                            <tr className="h-9 bg-surface-subtle text-[13px] font-[650] tabular-nums">
                              <td className="px-5">Total</td>
                              <td className="px-3 text-right">{sourceTotals.leads}</td>
                              <td className="px-3 text-right">{data.funnel[0]?.count ? Math.round(((data.funnel.find((f) => f.key === 'qualified')?.count ?? 0) / data.funnel[0].count) * 1000) / 10 : 0}%</td>
                              <td className="px-3 text-right">{sourceTotals.deals}</td>
                              <td className="px-3 text-right">{money(sourceTotals.wonValue)}</td>
                              <td className="px-5 text-right">{overallAvgDays ? Math.round(overallAvgDays) : '—'}</td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}
                </Card>

                <Card className="flex flex-col gap-4 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-0.5">
                      <h2 className="text-[15px] font-semibold text-ink">Win rate by rep</h2>
                      <span className="text-xs text-text-muted">Won ÷ closed opportunities in this window</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs text-text-muted">
                      <span className="h-0 w-2.5 border-t-2 border-dashed" style={{ borderColor: 'var(--color-amber-text)' }} />
                      Team {data.teamWinRatePct}%
                    </span>
                  </div>
                  {data.repWinRates.length === 0 ? (
                    <p className="text-sm text-text-muted">No closed opportunities in this window yet.</p>
                  ) : (
                    <div className="flex flex-col gap-3.5">
                      {data.repWinRates.map((rep) => (
                        <div key={rep.membershipId} className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="flex-1 font-semibold text-ink">{rep.displayName}</span>
                            <span className="text-xs tabular-nums text-text-muted">
                              {rep.won} / {rep.closed}
                            </span>
                            <span className="w-10 text-right font-[650] tabular-nums">{rep.winRatePct}%</span>
                          </div>
                          <div className="relative h-2 overflow-visible rounded-full bg-divider">
                            <div className="h-full rounded-full bg-primary" style={{ width: `${rep.winRatePct}%` }} />
                            <span className="absolute -top-0.5 h-3 w-0.5 rounded-full" style={{ left: `${data.teamWinRatePct}%`, background: 'var(--color-amber-text)' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {fastestSource && overallAvgDays && topRep && fastestSource.avgDaysToClose! < overallAvgDays && (
                    <div className="mt-auto flex items-start gap-2.5 rounded-input p-3" style={{ background: 'var(--color-primary-soft)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5M12 8h.01" />
                      </svg>
                      <span className="text-xs leading-4.25" style={{ color: 'var(--color-primary-soft-text)' }}>
                        {fastestSource.sourceName} leads close {Math.round(overallAvgDays - fastestSource.avgDaysToClose!)} days faster than average. {topRep.displayName} has this window's highest win rate — consider routing more there.
                      </span>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}
